/**
 * Privacy Policy page for AssistMD.
 * Public route — no auth required.
 * Required for Chrome Web Store listing.
 */
export function PrivacyPolicyPage(): JSX.Element {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="mb-12 text-center">
          <h1 className="font-serif text-3xl text-slate-900">Privacy Policy</h1>
          <p className="mt-2 text-sm text-slate-500">
            Last updated: February 21, 2026
          </p>
        </div>

        <div className="space-y-8 text-slate-700 leading-relaxed text-[0.95rem]">
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">1. Introduction</h2>
            <p>
              AssistMD (&quot;we&quot;, &quot;our&quot;, or &quot;the Service&quot;) is an AI-powered clinical
              documentation platform developed by Armada MD Healthcare Technologies Ltd. This Privacy
              Policy explains how we collect, use, and protect information when you use our Chrome
              extension, dashboard, and related services.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">2. Information We Collect</h2>
            <h3 className="font-medium text-slate-800 mb-2">2.1 Audio Data</h3>
            <p className="mb-3">
              When recording is explicitly started by the clinician, AssistMD captures audio from the
              browser tab for real-time transcription. Audio is streamed directly to our secure
              transcription service and is <strong>not stored</strong> after processing. Only the
              resulting text transcript is retained.
            </p>
            <h3 className="font-medium text-slate-800 mb-2">2.2 Clinical Documentation</h3>
            <p className="mb-3">
              Transcripts, SOAP notes, summaries, and other generated clinical documents are stored
              in our secure database to provide the Service. This data is associated with doctor and
              clinic identifiers, not directly with patient names.
            </p>
            <h3 className="font-medium text-slate-800 mb-2">2.3 Account Information</h3>
            <p>
              We collect email addresses and authentication credentials for account management.
              We do not collect payment information directly.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">3. How We Use Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide real-time transcription and clinical note generation</li>
              <li>To power SmartFill automation for EHR documentation</li>
              <li>To improve transcription accuracy and clinical note quality</li>
              <li>To maintain and improve the Service</li>
              <li>To communicate with you about your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">4. Data Security</h2>
            <p className="mb-3">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>All data in transit is encrypted using TLS 1.2+</li>
              <li>Data at rest is encrypted in our database (Supabase with Row Level Security)</li>
              <li>Audio streams are processed in real-time and not persisted</li>
              <li>WebSocket connections are authenticated and session-scoped</li>
              <li>Access controls enforce doctor-level data isolation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">5. HIPAA Compliance</h2>
            <p>
              AssistMD is designed with HIPAA compliance in mind. We implement technical safeguards
              including access controls, audit logging, encryption, and data isolation. Protected
              Health Information (PHI) is handled in accordance with the HIPAA Security Rule and
              Privacy Rule. We do not log PHI to application logs or external monitoring systems.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">6. Third-Party Services</h2>
            <p className="mb-3">We use the following third-party services to provide the Service:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Deepgram</strong> — Speech-to-text transcription</li>
              <li><strong>OpenAI / Anthropic</strong> — Clinical note generation (SOAP, summaries)</li>
              <li><strong>Supabase</strong> — Authentication and database</li>
              <li><strong>Cloudflare</strong> — CDN and DDoS protection</li>
              <li><strong>DigitalOcean</strong> — Server infrastructure</li>
            </ul>
            <p className="mt-3">
              Each provider processes data in accordance with their respective privacy policies
              and data processing agreements.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">7. Chrome Extension Permissions</h2>
            <p className="mb-3">The AssistMD Chrome extension requests the following permissions:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>activeTab / tabs</strong> — To detect EHR pages and inject the documentation overlay</li>
              <li><strong>tabCapture</strong> — To capture audio for transcription when recording is started</li>
              <li><strong>storage</strong> — To store extension settings locally</li>
              <li><strong>sidePanel</strong> — To display the clinical documentation panel</li>
              <li><strong>scripting</strong> — To interact with EHR forms for SmartFill</li>
              <li><strong>offscreen</strong> — To process audio in the background</li>
              <li><strong>notifications</strong> — To alert about recording status and clinical alerts</li>
            </ul>
            <p className="mt-3">
              The extension only activates when explicitly triggered by the clinician. It does not
              collect data passively or operate on pages outside of active clinical sessions.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">8. Data Retention</h2>
            <p>
              Clinical documents (transcripts, SOAP notes) are retained for the duration of the
              clinical relationship. Audio data is not retained — it is processed in real-time
              and discarded. Account data is retained until the account is deleted. You may request
              deletion of your data at any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">9. Your Rights</h2>
            <p className="mb-3">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access your personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent for audio recording at any time</li>
              <li>Export your clinical documents</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">10. Contact</h2>
            <p>
              For privacy-related inquiries, data requests, or concerns, contact us at:
            </p>
            <p className="mt-2 font-medium">
              Armada MD Healthcare Technologies Ltd.<br />
              Email: privacy@assistmd.io
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify registered users
              of material changes via email. Continued use of the Service after changes constitutes
              acceptance of the updated policy.
            </p>
          </section>
        </div>

        <div className="mt-16 border-t border-slate-200 pt-8 text-center text-xs text-slate-400">
          <p>&copy; 2026 Armada MD Healthcare Technologies Ltd. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
